import config from '../config.js';

/**
 * @typedef {import('../types.js').ApiResponse} ApiResponse
 * @typedef {import('../types.js').SessionData} SessionData
 * @typedef {import('../types.js').RecommendationResponse} RecommendationResponse
 * @typedef {import('../types.js').FormData} FormData
 */

class ApiService {
  constructor() {
    this.baseUrl = config.api.baseUrl;
  }

  /**
   * Start or resume questionnaire session
   * @param {string} firebaseToken - Firebase ID token from authentication
   * @param {string} email - Optional user email
   * @param {Object} profile - Optional user profile { nationality, employment_type, salary_range }
   * @returns {Promise<SessionData>}
   */
  async startQuestionnaire(firebaseToken, email = null, profile = {}) {
    const response = await fetch(`${this.baseUrl}/credit-cards/recommend/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${firebaseToken}`,
      },
      body: JSON.stringify({
        email: email || null,
        nationality: profile.nationality || null,
        employment_type: profile.employment_type || null,
        salary_range: profile.salary_range || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to start questionnaire');
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.message || 'API returned an error');
    }

    return data.data;
  }

  /**
   * Save questionnaire progress
   * @param {string} sessionId - Session ID
   * @param {FormData} responses - Form responses
   * @returns {Promise<ApiResponse>}
   */
  async saveProgress(sessionId, responses) {
    const response = await fetch(`${this.baseUrl}/credit-cards/recommend/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        responses: responses,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save progress');
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.message || 'API returned an error while saving');
    }

    return data;
  }

  /**
   * Complete questionnaire and get recommendations
   * @param {string} sessionId - Session ID
   * @returns {Promise<RecommendationResponse>}
   */
  async completeQuestionnaire(sessionId) {
    const response = await fetch(`${this.baseUrl}/credit-cards/recommend/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to complete questionnaire');
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.message || 'API returned an error during completion');
    }

    return data.data;
  }

  async status(sessionId) {
    const response = await fetch(`${this.baseUrl}/credit-cards/recommend/status/${sessionId}`);

    if (!response.ok) {
      throw new Error('Failed to check status');
    }

    const data = await response.json();
    return data.data; // Returns { completed: true/false }
  }

  async getAnalysisDashboard(sessionId) {
      const response = await fetch(`${this.baseUrl}/credit-cards/analysis/${sessionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.detail || 'Failed to get analysis');
        error.statusCode = response.status; // Attach status code to error
        throw error;
      }

      const data = await response.json();

      if (data.error) {
        const error = new Error(data.message || 'API returned an error');
        error.statusCode = response.status;
        throw error;
      }

      // Ensure we never return undefined - React Query doesn't like it
      if (data.data === undefined || data.data === null) {
        const error = new Error('No data returned from API');
        error.statusCode = response.status;
        throw error;
      }

      return data.data;
  }

  /**
   * Get profile question options (nationality, employment, salary)
   * @returns {Promise<Object>}
   */
  async getProfileOptions() {
    const response = await fetch(`${this.baseUrl}/questions/profile-options`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to get profile options');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get user profile
   * @param {string} firebaseToken - Firebase ID token
   * @returns {Promise<Object>}
   */
  async getUserProfile(firebaseToken) {
    const response = await fetch(`${this.baseUrl}/users/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${firebaseToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to get profile');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Update user profile
   * @param {string} firebaseToken - Firebase ID token
   * @param {Object} profile - Profile data
   * @returns {Promise<Object>}
   */
  async updateUserProfile(firebaseToken, profile) {
    const response = await fetch(`${this.baseUrl}/users/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${firebaseToken}`,
      },
      body: JSON.stringify(profile),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update profile');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get user's completed sessions
   * @param {string} firebaseToken - Firebase ID token
   * @returns {Promise<Array>}
   */
  async getUserSessions(firebaseToken) {
    const response = await fetch(`${this.baseUrl}/users/sessions`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${firebaseToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to get sessions');
    }

    const data = await response.json();
    return data.data;
  }
}

export const queryKeys = {
  questionnaire: phoneNumber => ['questionnaire', phoneNumber],
  session: sessionId => ['session', sessionId],
  recommendations: sessionId => ['recommendations', sessionId],
  profile: () => ['profile'],
  userSessions: () => ['userSessions'],
};

export const apiService = new ApiService();
export default apiService;
